import { createSlice, type PayloadAction } from '@reduxjs/toolkit';

/**
 * ClaimScrubber UI State
 * Only manages UI state - server state stays in RTK Query, form state stays in React Hook Form
 */
export interface ClaimScrubberUIState {
  panel: {
    isOpen: boolean;
    position: 'bottom' | 'sidebar';
    isCollapsed: boolean; // For sidebar mode only
    width: number;        // Sidebar width in pixels
  };
  navigation: {
    highlightedField: string | null; // Currently highlighted form field
    highlightedBox: string | null;   // Currently highlighted CMS box number
  };
  preferences: {
    autoOpenOnErrors: boolean;       // Auto-open panel when errors found
    persistPosition: boolean;        // Remember panel position preference
    persistWidth: boolean;           // Remember sidebar width
    /* "Show archived and snoozed" on the Claims queues (GDN-3381). It rides
       the feature's user-scoped redux persist: practices share machines, and
       one biller's choice to work with hidden rows on screen must not follow
       the next person who signs in. */
    showHiddenClaimRows: boolean;
  };
}

const initialState: ClaimScrubberUIState = {
  panel: {
    isOpen: false,
    position: 'sidebar',
    isCollapsed: false,
    width: 400, // Default width in pixels
  },
  navigation: {
    highlightedField: null,
    highlightedBox: null,
  },
  preferences: {
    autoOpenOnErrors: true,
    persistPosition: true,
    persistWidth: true,
    showHiddenClaimRows: false,
  },
};

export const claimScrubberSlice = createSlice({
  name: 'claimScrubber',
  initialState,
  reducers: {
    // Panel actions
    togglePanel: (state) => {
      state.panel.isOpen = !state.panel.isOpen;
    },
    
    openPanel: (state) => {
      state.panel.isOpen = true;
    },
    
    closePanel: (state) => {
      state.panel.isOpen = false;
    },
    
    setPanelPosition: (state, action: PayloadAction<'bottom' | 'sidebar'>) => {
      state.panel.position = action.payload;
    },
    
    togglePanelCollapse: (state) => {
      state.panel.isCollapsed = !state.panel.isCollapsed;
    },
    
    setPanelWidth: (state, action: PayloadAction<number>) => {
      // Clamp width between min and max
      const width = Math.min(Math.max(action.payload, 280), 600);
      state.panel.width = width;
    },
    
    // Navigation actions
    highlightField: (
      state,
      action: PayloadAction<{ field: string; box: string | null }>
    ) => {
      state.navigation.highlightedField = action.payload.field;
      state.navigation.highlightedBox = action.payload.box;
    },
    
    clearHighlight: (state) => {
      state.navigation.highlightedField = null;
      state.navigation.highlightedBox = null;
    },
    
    // Preference actions
    setAutoOpenOnErrors: (state, action: PayloadAction<boolean>) => {
      state.preferences.autoOpenOnErrors = action.payload;
    },
    
    setPersistPosition: (state, action: PayloadAction<boolean>) => {
      state.preferences.persistPosition = action.payload;
    },

    setShowHiddenClaimRows: (state, action: PayloadAction<boolean>) => {
      state.preferences.showHiddenClaimRows = action.payload;
    },
    
    // Combined actions
    navigateToField: (
      state,
      action: PayloadAction<{ field: string; box?: string | null }>
    ) => {
      // Set highlight
      state.navigation.highlightedField = action.payload.field;
      state.navigation.highlightedBox = action.payload.box || null;
      
      // Ensure panel is open
      state.panel.isOpen = true;
      
      // If in sidebar mode and collapsed, expand it
      if (state.panel.position === 'sidebar' && state.panel.isCollapsed) {
        state.panel.isCollapsed = false;
      }
    },
  },
});

// Export actions
export const {
  togglePanel,
  openPanel,
  closePanel,
  setPanelPosition,
  togglePanelCollapse,
  setPanelWidth,
  highlightField,
  clearHighlight,
  setAutoOpenOnErrors,
  setPersistPosition,
  setShowHiddenClaimRows,
  navigateToField,
} = claimScrubberSlice.actions;

// Export reducer
export default claimScrubberSlice.reducer;